// Builds the (compact) data context and system prompt for the AI companion,
// and parses the action proposals a reply may carry. Everything the model sees
// is assembled here, on the device, from the panels the user allowed.

export const SCOPE_LABELS = {
  challenge: "Challenge & calendar",
  goals: "Goals & sub-goals",
  reminders: "Reminders",
  stickyNotes: "Sticky notes",
  remember: "Remember",
  clipboard: "Clipboard",
  quickLinks: "Quick links",
  conferences: "Conferences",
  publications: "Publications",
};
export const ALL_SCOPES = Object.keys(SCOPE_LABELS);

// Panel key (data-panel-key) → scope.
export const PANEL_SCOPE = {
  important: "goals",
  reminders: "reminders",
  goals: "goals",
  stickyNotes: "stickyNotes",
  remember: "remember",
  clipboard: "clipboard",
  quickLinks: "quickLinks",
  conferences: "conferences",
  publications: "publications",
  calendar: "challenge",
};

const MAX_ITEMS = 120;
const clip = (s, n = 160) => {
  const t = String(s || "")
    .replace(/\s+/g, " ")
    .trim();
  return t.length > n ? t.slice(0, n - 1) + "…" : t;
};
const day = (d) => (d ? String(d).slice(0, 10) : "");
const box = (done) => (done ? "[x]" : "[ ]");

function section(title, lines, total) {
  if (!lines.length) return `## ${title}\n(none)\n`;
  const more = total > lines.length ? `\n(+${total - lines.length} more not shown)` : "";
  return `## ${title}\n${lines.join("\n")}${more}\n`;
}

/**
 * Compact text view of the user's data, limited to `scopes`. `focus` (a scope)
 * is listed first and gets more room.
 */
export function buildAiContext(state, scopes, focus) {
  const allowed = new Set(scopes && scopes.length ? scopes : ALL_SCOPES);
  const parts = [];
  const today = state.today || new Date().toISOString().slice(0, 10);
  const limit = (scope) => (scope === focus ? MAX_ITEMS * 2 : MAX_ITEMS);

  if (allowed.has("challenge")) {
    const s = state.settings || {};
    const done = new Set(state.completedDays || []);
    const partial = new Set(state.partialDays || []);
    const dates = state.challengeDates || [];
    const past = dates.filter((d) => d.date <= today);
    const missed = past.filter((d) => !done.has(d.date) && !partial.has(d.date) && !d.isToday).length;
    const last14 = dates
      .filter((d) => d.date <= today)
      .slice(-14)
      .map((d) => `${d.date}:${done.has(d.date) ? "done" : partial.has(d.date) ? "partial" : d.isToday ? "today" : "missed"}`);
    parts.push(
      section(
        "Challenge",
        [
          `Name: ${clip(s.challengeName, 80)}`,
          `Start: ${day(s.startDate)} · Length: ${s.numberOfDays} days · Today: ${today}`,
          `Days done: ${done.size} · partial: ${partial.size} · missed so far: ${missed} · elapsed: ${past.length}`,
          `Streak: ${state.streak ?? 0} (best ${state.bestStreak ?? 0})`,
          `Last 14 days: ${last14.join(", ") || "(not started)"}`,
        ],
        5,
      ),
    );
  }

  if (allowed.has("goals")) {
    const lines = [];
    let total = 0;
    const subs = state.subGoals || [];
    for (const tab of state.tabs || []) {
      lines.push(`# Tab "${clip(tab.name, 40)}" (id ${tab.id})`);
      for (const g of tab.goals || []) {
        total++;
        if (lines.length > limit("goals")) continue;
        lines.push(
          `- ${box(g.completed)} ${clip(g.text)} (id ${g.id}${g.deadline ? `, due ${day(g.deadline)}` : ""}${g.important ? ", important" : ""})`,
        );
        for (const sg of subs.filter((x) => String(x.goalId) === String(g.id))) {
          lines.push(
            `    - ${box(sg.completed)} ${clip(sg.text)} (sub-goal id ${sg.id}${sg.deadline ? `, due ${day(sg.deadline)}` : ""})`,
          );
        }
      }
    }
    parts.push(section("Goals (tabs → goals → sub-goals)", lines.slice(0, limit("goals") + 20), total));
  }

  if (allowed.has("reminders")) {
    const items = state.reminders || [];
    parts.push(
      section(
        "Reminders (due soon, across panels)",
        items.slice(0, limit("reminders")).map((r) => `- ${r.kind}: ${clip(r.text)}${r.due ? ` (due ${day(r.due)})` : ""}`),
        items.length,
      ),
    );
  }

  if (allowed.has("stickyNotes")) {
    const notes = state.stickyNotes || [];
    parts.push(
      section(
        "Sticky notes",
        notes.slice(0, limit("stickyNotes")).map(
          (n) =>
            `- (id ${n.id}, ${n.color}${n.dueDate ? `, due ${day(n.dueDate)}` : ""}${n.anchor ? `, pinned to ${n.anchor}` : ""}) ${clip(n.text, 400)}`,
        ),
        notes.length,
      ),
    );
  }

  if (allowed.has("remember")) {
    const items = state.rememberItems || [];
    parts.push(
      section(
        "Remember",
        items.slice(0, limit("remember")).map(
          (r) =>
            `- ${box(r.completed)} ${clip(r.text)} (id ${r.id}${r.dueDate ? `, due ${day(r.dueDate)}` : ""}${r.recurrence ? `, repeats weekdays ${(r.recurrence.weekdays || []).join("/")} until ${day(r.recurrence.endDate)}` : ""})`,
        ),
        items.length,
      ),
    );
  }

  if (allowed.has("clipboard")) {
    const cards = state.clipboardCards || [];
    parts.push(
      section(
        "Clipboard cards",
        cards.slice(0, limit("clipboard")).map((c) => {
          const body = Array.isArray(c.blocks) && c.blocks.length
            ? c.blocks.map((b) => `${b.type === "todo" ? box(b.checked) + " " : ""}${b.text}`).join(" / ")
            : c.content;
          return `- "${clip(c.title, 80)}" (id ${c.id}): ${clip(body, 300)}`;
        }),
        cards.length,
      ),
    );
  }

  if (allowed.has("quickLinks")) {
    const cards = state.quickLinkCards || [];
    parts.push(
      section(
        "Quick link cards",
        cards.slice(0, limit("quickLinks")).map(
          (c) =>
            `- ${box(c.completed)} "${clip(c.title, 80)}" (id ${c.id}${c.dueDate ? `, due ${day(c.dueDate)}` : ""}): ${(c.links || [])
              .slice(0, 6)
              .map((l) => `${box(l.completed)} ${clip(l.label || l.title || l.url, 60)}`)
              .join("; ")}`,
        ),
        cards.length,
      ),
    );
  }

  if (allowed.has("conferences")) {
    const cards = state.conferenceCards || [];
    parts.push(
      section(
        "Conferences",
        cards.slice(0, limit("conferences")).map(
          (c) =>
            `- ${clip(c.title, 80)}${c.titleExpanded ? ` (${clip(c.titleExpanded, 80)})` : ""} (id ${c.id}): ${(c.entries || [])
              .slice(0, 5)
              .map((e) => `${clip(e.label || e.type || "deadline", 40)} ${day(e.date)}${e.submitted ? " (submitted)" : ""}`)
              .join("; ")}`,
        ),
        cards.length,
      ),
    );
  }

  if (allowed.has("publications")) {
    const pubs = state.publications || [];
    parts.push(
      section(
        "Publications",
        pubs.slice(0, limit("publications")).map(
          (p) =>
            `- "${clip(p.title, 100)}" (id ${p.id}) status ${p.status}${p.venueType === "journal" ? ` · journal ${clip(p.journalName, 60)}` : ` · ${clip(p.conferenceName, 60)}`}${p.submittedDate ? ` · submitted ${day(p.submittedDate)}` : ""}${p.expectedDecisionDate ? ` · decision expected ${day(p.expectedDecisionDate)}` : ""}`,
        ),
        pubs.length,
      ),
    );
  }

  // Focused scope first.
  if (focus) {
    const title = SCOPE_LABELS[focus];
    const idx = parts.findIndex((p) => title && p.startsWith(`## ${title.split(" ")[0]}`));
    if (idx > 0) parts.unshift(parts.splice(idx, 1)[0]);
  }
  return parts.join("\n");
}

export const ACTION_SPEC = `
When the user asks you to add, change, complete or remove something, DO NOT claim it is done. Instead propose actions by ending your reply with ONE fenced block:
\`\`\`xz-actions
[ { "type": "...", ... }, ... ]
\`\`\`
The app shows each proposal with a Confirm button; nothing changes until the user confirms. Supported action objects (fields in parentheses are optional):
- {"type":"add_goal","text":"...",("tab":"tab name or id"),("deadline":"YYYY-MM-DD")}
- {"type":"add_sub_goal","goal":"goal id or exact text","text":"...",("deadline":"YYYY-MM-DD")}
- {"type":"complete_goal","goal":"goal id or exact text"} / {"type":"uncomplete_goal","goal":"..."}
- {"type":"complete_sub_goal","subGoal":"id or exact text"} / {"type":"uncomplete_sub_goal","subGoal":"..."}
- {"type":"set_goal_deadline","goal":"id or text","deadline":"YYYY-MM-DD" or null}
- {"type":"delete_goal","goal":"id or text"} / {"type":"delete_sub_goal","subGoal":"id or text"}
- {"type":"add_sticky_note","text":"markdown text",("color":"yellow|pink|blue|green|purple"),("dueDate":"YYYY-MM-DD")}
- {"type":"update_sticky_note","note":"id","text":"new full text"} / {"type":"delete_sticky_note","note":"id"}
- {"type":"add_remember","text":"...",("dueDate":"YYYY-MM-DD")} / {"type":"complete_remember","item":"id or text"} / {"type":"delete_remember","item":"id or text"}
- {"type":"add_clipboard_card","title":"...","content":"..."} / {"type":"delete_clipboard_card","card":"id or title"}
- {"type":"add_quick_link_card","title":"...","links":[{"label":"...","url":"https://..."}]}
- {"type":"mark_day","date":"YYYY-MM-DD","status":"complete|partial|none"}
Keep the block valid JSON (double quotes, no comments). Omit the block entirely when nothing should change.`;

/** System prompt for one turn. */
export function buildSystemPrompt({ name, context, focusLabel, today, userName }) {
  return `You are ${name}, the friendly in-app companion inside Xunzhine, a day-challenge tracker${userName ? ` used by ${userName}` : ""}. Today is ${today}.
You can see the user's current data below (only the panels they allowed). Answer questions about it precisely, help plan, summarise, rewrite, translate, or give a short briefing when asked. Be concise and warm; use short Markdown (lists, bold) where it helps. Never invent items that are not in the data. If something is outside the data, say so.
${focusLabel ? `The user opened you from the "${focusLabel}" panel — prioritise that panel unless they ask about something else.\n` : ""}
${ACTION_SPEC}

# USER DATA
${context}`;
}

/** Split a reply into visible text + proposed actions. */
export function parseActions(text) {
  const re = /```xz-actions\s*([\s\S]*?)```/i;
  const m = text.match(re);
  if (!m) return { clean: text.trim(), actions: [] };
  let actions = [];
  try {
    const parsed = JSON.parse(m[1].trim());
    actions = Array.isArray(parsed) ? parsed.filter((a) => a && typeof a.type === "string") : [];
  } catch (e) {
    actions = [];
  }
  return { clean: text.replace(re, "").trim(), actions };
}

/** Short human label for an action card. */
export function describeAction(a) {
  const t = a.type;
  const q = (s) => (s ? `“${clip(s, 60)}”` : "");
  switch (t) {
    case "add_goal":
      return `Add goal ${q(a.text)}${a.tab ? ` to ${q(a.tab)}` : ""}${a.deadline ? ` due ${a.deadline}` : ""}`;
    case "add_sub_goal":
      return `Add sub-goal ${q(a.text)} under ${q(a.goal)}`;
    case "complete_goal":
      return `Mark goal ${q(a.goal)} complete`;
    case "uncomplete_goal":
      return `Mark goal ${q(a.goal)} not complete`;
    case "complete_sub_goal":
      return `Mark sub-goal ${q(a.subGoal)} complete`;
    case "uncomplete_sub_goal":
      return `Mark sub-goal ${q(a.subGoal)} not complete`;
    case "set_goal_deadline":
      return `Set deadline of ${q(a.goal)} to ${a.deadline || "none"}`;
    case "delete_goal":
      return `Delete goal ${q(a.goal)}`;
    case "delete_sub_goal":
      return `Delete sub-goal ${q(a.subGoal)}`;
    case "add_sticky_note":
      return `Add a ${a.color || "yellow"} sticky note ${q(a.text)}`;
    case "update_sticky_note":
      return `Rewrite sticky note ${q(a.note)}: ${q(a.text)}`;
    case "delete_sticky_note":
      return `Delete sticky note ${q(a.note)}`;
    case "add_remember":
      return `Add to Remember ${q(a.text)}${a.dueDate ? ` due ${a.dueDate}` : ""}`;
    case "complete_remember":
      return `Tick Remember item ${q(a.item)}`;
    case "delete_remember":
      return `Delete Remember item ${q(a.item)}`;
    case "add_clipboard_card":
      return `Add clipboard card ${q(a.title)}`;
    case "delete_clipboard_card":
      return `Delete clipboard card ${q(a.card)}`;
    case "add_quick_link_card":
      return `Add quick-link card ${q(a.title)} (${(a.links || []).length} links)`;
    case "mark_day":
      return `Mark ${a.date} as ${a.status}`;
    default:
      return `Unknown action “${t}”`;
  }
}

export const BRIEFING_PROMPT =
  "Give me a short briefing for today: how the challenge is going, what is due today or overdue, the most important open goals, and one encouraging line. Keep it under 180 words.";
